import { LitElement, html, nothing, type CSSResultGroup } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { emit } from '../utils/dom.utils.js';
import { type MessageSeverity } from '../message/index.js';
import styles from './styles.js';

// ─── Public types ─────────────────────────────────────────────────────────────

/** Internal file entry tracking an uploaded file's metadata and preview URL. */
export interface UwcFileEntry {
  id:        string;
  file:      File;
  objectUrl: string | null;
}

/** Detail for `uwc-select` — fired whenever new files are added to the queue. */
export interface FileUploadSelectEvent { files: File[]; }

/** Detail for `uwc-upload` — fired when XHR upload completes successfully. */
export interface FileUploadUploadEvent { files: File[]; xhr: XMLHttpRequest; }

/** Detail for `uwc-upload-error` — fired when XHR upload fails. */
export interface FileUploadErrorEvent  { files: File[]; xhr: XMLHttpRequest; error: string; }

/** Detail for `uwc-upload-progress` — fired on each XHR progress tick. */
export interface FileUploadProgressEvent { files: File[]; progress: number; originalEvent: ProgressEvent; }

/** Detail for `uwc-remove` — fired when a single file is removed from the queue. */
export interface FileUploadRemoveEvent { file: File; }

/** Detail for `uwc-clear` — fired when all files are cleared. */
export interface FileUploadClearEvent {}

/** Detail for `uwc-before-upload` — fired just before the XHR is sent. */
export interface FileUploadBeforeUploadEvent { formData: FormData; xhr: XMLHttpRequest; }

/** Detail for `uwc-upload-handler` — fired instead of XHR when `customUpload` is set. */
export interface FileUploadHandlerEvent { files: File[]; }

// ─── Internal ─────────────────────────────────────────────────────────────────

interface _Msg { id: string; severity: MessageSeverity; text: string; }

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * uwc-fileupload
 *
 * Production-grade file upload component matching PrimeNG's FileUpload API.
 * Supports two modes — `advanced` (toolbar + drag-and-drop panel, default) and
 * `basic` (a single Choose button). Validation of file type, size, and count
 * is built-in and produces dismissible inline messages.
 *
 * @attr {string}  name                    - FormData field name sent to the server. Default: 'file'.
 * @attr {string}  url                     - Server endpoint for XHR upload.
 * @attr {string}  method                  - HTTP method: post | put. Default: post.
 * @attr {boolean} multiple                - Allow selecting multiple files.
 * @attr {string}  accept                  - Comma-separated MIME types / extensions.
 * @attr {boolean} disabled                - Disable all interaction.
 * @attr {boolean} auto                    - Upload immediately after selection (requires `url` or `custom-upload`).
 * @attr {number}  max-file-size           - Maximum bytes per file (0 = unlimited).
 * @attr {number}  file-limit             - Maximum number of files in the queue (0 = unlimited).
 * @attr {string}  mode                    - advanced | basic. Default: advanced.
 * @attr {boolean} custom-upload           - Fire `uwc-upload-handler` instead of XHR.
 * @attr {boolean} show-upload-button      - Show the Upload button. Default: true.
 * @attr {boolean} show-cancel-button      - Show the Cancel button. Default: true.
 * @attr {string}  choose-label            - Choose button label. Default: Choose.
 * @attr {string}  upload-label            - Upload button label. Default: Upload.
 * @attr {string}  cancel-label            - Cancel button label. Default: Cancel.
 * @attr {string}  choose-icon             - uwc-icon name for Choose button. Default: plus-lg.
 * @attr {string}  upload-icon             - uwc-icon name for Upload button. Default: cloud-upload.
 * @attr {string}  cancel-icon             - uwc-icon name for Cancel button. Default: x-lg.
 * @attr {number}  preview-width           - Thumbnail size in px. Default: 50.
 *
 * @prop {File[]} files - Read-only: current files in the queue.
 *
 * @fires uwc-select           — Files added: `{ files }`.
 * @fires uwc-upload           — XHR upload succeeded: `{ files, xhr }`.
 * @fires uwc-upload-error     — XHR upload failed: `{ files, xhr, error }`.
 * @fires uwc-upload-progress  — XHR progress tick: `{ files, progress, originalEvent }`.
 * @fires uwc-remove           — Single file removed: `{ file }`.
 * @fires uwc-clear            — All files cleared.
 * @fires uwc-before-upload    — Just before XHR send: `{ formData, xhr }`.
 * @fires uwc-upload-handler   — Custom upload (when `customUpload`): `{ files }`.
 *
 * @csspart base           - Outer container.
 * @csspart toolbar        - Toolbar row (advanced mode).
 * @csspart choose-button  - Choose button.
 * @csspart upload-button  - Upload button.
 * @csspart cancel-button  - Cancel button.
 * @csspart content        - Drop-zone / content area.
 * @csspart empty          - Empty-state wrapper.
 * @csspart messages       - Validation messages container.
 * @csspart files          - File list wrapper.
 * @csspart file-row       - Single file row.
 * @csspart thumbnail      - File thumbnail cell.
 * @csspart file-info      - Filename + size cell.
 * @csspart remove-button  - Per-row remove button.
 * @csspart progress       - Progress bar track.
 * @csspart progress-bar   - Progress bar fill.
 *
 * @cssprop {length} --uwc-fileupload-width            - Host width. Default: 100%.
 * @cssprop {length} --uwc-fileupload-height           - Min-height of content area. Default: 160px.
 * @cssprop {color}  --uwc-fileupload-border           - Panel border colour.
 * @cssprop {length} --uwc-fileupload-radius           - Panel border radius.
 * @cssprop {color}  --uwc-fileupload-toolbar-bg       - Toolbar background.
 * @cssprop {length} --uwc-fileupload-progress-height  - Progress bar height. Default: 4px.
 * @cssprop {color}  --uwc-fileupload-progress-color   - Progress bar fill colour.
 * @cssprop {color}  --uwc-fileupload-progress-track   - Progress bar track colour.
 *
 * @slot header - Replace the entire toolbar with custom content.
 * @slot empty  - Replace the default empty-state icon + text.
 */
export class UwcFileupload extends LitElement {
  static override styles: CSSResultGroup = [styles];

  @query('.uwc-fileupload__input') private _input!: HTMLInputElement;

  // ── Attributes ───────────────────────────────────────────────────────────────

  @property() name = 'file';
  @property() url  = '';
  @property() method: 'post' | 'put' = 'post';

  @property({ type: Boolean, reflect: true }) multiple = false;
  @property() accept = '';
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean }) auto = false;

  @property({ type: Number, attribute: 'max-file-size' })  maxFileSize = 0;
  @property({ type: Number, attribute: 'file-limit' })     fileLimit   = 0;

  @property({ reflect: true }) mode: 'advanced' | 'basic' = 'advanced';

  @property({ type: Boolean, attribute: 'custom-upload' })       customUpload      = false;
  @property({ type: Boolean, attribute: 'show-upload-button' })  showUploadButton  = true;
  @property({ type: Boolean, attribute: 'show-cancel-button' })  showCancelButton  = true;

  @property({ attribute: 'choose-label' }) chooseLabel = 'Choose';
  @property({ attribute: 'upload-label' }) uploadLabel = 'Upload';
  @property({ attribute: 'cancel-label' }) cancelLabel = 'Cancel';

  @property({ attribute: 'choose-icon' }) chooseIcon = 'plus-lg';
  @property({ attribute: 'upload-icon' }) uploadIcon = 'cloud-upload';
  @property({ attribute: 'cancel-icon' }) cancelIcon = 'x-lg';

  @property({ type: Number, attribute: 'preview-width' }) previewWidth = 50;

  // Validation message templates — match defaults, {0} is the placeholder.
  @property({ attribute: 'invalid-file-size-message-summary' })
    invalidFileSizeMessageSummary = '{0}: Invalid file size,';
  @property({ attribute: 'invalid-file-size-message-detail' })
    invalidFileSizeMessageDetail = 'maximum upload size is {0}.';
  @property({ attribute: 'invalid-file-type-message-summary' })
    invalidFileTypeMessageSummary = '{0}: Invalid file type,';
  @property({ attribute: 'invalid-file-type-message-detail' })
    invalidFileTypeMessageDetail = 'allowed file types: {0}.';
  @property({ attribute: 'invalid-file-limit-message-summary' })
    invalidFileLimitMessageSummary = 'Maximum number of files exceeded,';
  @property({ attribute: 'invalid-file-limit-message-detail' })
    invalidFileLimitMessageDetail = 'limit is {0} at most.';

  // ── State ────────────────────────────────────────────────────────────────────

  @state() private _entries:   UwcFileEntry[] = [];
  @state() private _dragOver:  boolean        = false;
  @state() private _msgs:      _Msg[]         = [];
  @state() private _uploading: boolean        = false;
  @state() private _progress:  number         = 0;

  // ── Utilities ────────────────────────────────────────────────────────────────

  private _uid(): string {
    return `fu-${Math.random().toString(36).slice(2, 9)}`;
  }

  private _fmt(template: string, ...args: string[]): string {
    return args.reduce((t, v, i) => t.replace(new RegExp(`\\{${i}\\}`, 'g'), v), template);
  }

  private _formatSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const i     = Math.floor(Math.log(bytes) / Math.log(1024));
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    return `${(bytes / Math.pow(1024, Math.min(i, 4))).toFixed(1)} ${units[Math.min(i, 4)]}`;
  }

  private _isImage(file: File): boolean {
    return file.type.startsWith('image/');
  }

  private _isAccepted(file: File): boolean {
    if (!this.accept) return true;
    return this.accept.split(',').map(a => a.trim()).some(accept => {
      if (accept.startsWith('.'))   return file.name.toLowerCase().endsWith(accept.toLowerCase());
      if (accept.endsWith('/*'))    return file.type.startsWith(accept.slice(0, -2));
      return file.type === accept;
    });
  }

  // ── File management ──────────────────────────────────────────────────────────

  private _addFiles(rawFiles: File[]): void {
    const msgs: _Msg[]  = [];
    const valid: File[] = [];

    for (const file of rawFiles) {
      if (!this._isAccepted(file)) {
        msgs.push({
          id:       this._uid(),
          severity: 'error',
          text: `${this._fmt(this.invalidFileTypeMessageSummary, file.name)} ${this._fmt(this.invalidFileTypeMessageDetail, this.accept)}`,
        });
        continue;
      }
      if (this.maxFileSize > 0 && file.size > this.maxFileSize) {
        msgs.push({
          id:       this._uid(),
          severity: 'error',
          text: `${this._fmt(this.invalidFileSizeMessageSummary, file.name)} ${this._fmt(this.invalidFileSizeMessageDetail, this._formatSize(this.maxFileSize))}`,
        });
        continue;
      }
      valid.push(file);
    }

    if (this.multiple) {
      const slots = this.fileLimit > 0 ? this.fileLimit - this._entries.length : valid.length;
      if (slots <= 0 || (this.fileLimit > 0 && this._entries.length >= this.fileLimit)) {
        msgs.push({
          id:       this._uid(),
          severity: 'warn',
          text: `${this.invalidFileLimitMessageSummary} ${this._fmt(this.invalidFileLimitMessageDetail, String(this.fileLimit))}`,
        });
        this._msgs = [...this._msgs, ...msgs];
        return;
      }
      const toAdd = valid.slice(0, slots);
      if (valid.length > slots) {
        msgs.push({
          id:       this._uid(),
          severity: 'warn',
          text: `${this.invalidFileLimitMessageSummary} ${this._fmt(this.invalidFileLimitMessageDetail, String(this.fileLimit))}`,
        });
      }
      this._entries = [
        ...this._entries,
        ...toAdd.map(f => ({
          id:        this._uid(),
          file:      f,
          objectUrl: this._isImage(f) ? URL.createObjectURL(f) : null,
        })),
      ];
    } else {
      // Single mode: replace existing entry.
      this._revokeAll();
      const take = valid.slice(0, 1);
      this._entries = take.map(f => ({
        id:        this._uid(),
        file:      f,
        objectUrl: this._isImage(f) ? URL.createObjectURL(f) : null,
      }));
    }

    this._msgs = [...this._msgs, ...msgs];

    if (this._entries.length > 0) {
      emit(this, 'uwc-select', { files: this._entries.map(e => e.file) } satisfies FileUploadSelectEvent);
    }

    if (this.auto && this._entries.length > 0) {
      if (this.customUpload) {
        emit(this, 'uwc-upload-handler', { files: this._entries.map(e => e.file) } satisfies FileUploadHandlerEvent);
      } else if (this.url) {
        this._doUpload();
      }
    }
  }

  private _removeEntry(entry: UwcFileEntry): void {
    if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl);
    this._entries = this._entries.filter(e => e.id !== entry.id);
    if (this._input) this._input.value = '';
    emit(this, 'uwc-remove', { file: entry.file } satisfies FileUploadRemoveEvent);
  }

  private _revokeAll(): void {
    this._entries.forEach(e => { if (e.objectUrl) URL.revokeObjectURL(e.objectUrl); });
  }

  // ── XHR upload ───────────────────────────────────────────────────────────────

  private _doUpload(): void {
    if (!this.url || this._entries.length === 0) return;

    const files = this._entries.map(e => e.file);
    const xhr   = new XMLHttpRequest();
    const fd    = new FormData();

    files.forEach(f => fd.append(this.name || 'file', f, f.name));

    emit(this, 'uwc-before-upload', { formData: fd, xhr } satisfies FileUploadBeforeUploadEvent);

    this._uploading = true;
    this._progress  = 0;

    xhr.upload.addEventListener('progress', (e: ProgressEvent) => {
      if (!e.lengthComputable) return;
      this._progress = Math.round((e.loaded / e.total) * 100);
      emit(this, 'uwc-upload-progress', { files, progress: this._progress, originalEvent: e } satisfies FileUploadProgressEvent);
    });

    xhr.addEventListener('load', () => {
      this._uploading = false;
      if (xhr.status >= 200 && xhr.status < 300) {
        emit(this, 'uwc-upload', { files, xhr } satisfies FileUploadUploadEvent);
        this.clear();
      } else {
        emit(this, 'uwc-upload-error', { files, xhr, error: `Server responded with ${xhr.status}` } satisfies FileUploadErrorEvent);
      }
    });

    xhr.addEventListener('error', () => {
      this._uploading = false;
      emit(this, 'uwc-upload-error', { files, xhr, error: 'Network error' } satisfies FileUploadErrorEvent);
    });

    xhr.addEventListener('abort', () => { this._uploading = false; });

    xhr.open(this.method.toUpperCase(), this.url);
    xhr.send(fd);
  }

  // ── DOM event handlers ────────────────────────────────────────────────────────

  private _onInputChange(e: Event): void {
    const input = e.target as HTMLInputElement;
    if (input.files?.length) this._addFiles(Array.from(input.files));
    input.value = '';
  }

  private _openChooser(): void {
    if (!this.disabled) this._input?.click();
  }

  private _onDragEnter(e: DragEvent): void {
    e.preventDefault();
    if (!this.disabled) this._dragOver = true;
  }

  private _onDragOver(e: DragEvent): void {
    e.preventDefault();
    if (this.disabled) return;
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    this._dragOver = true;
  }

  private _onDragLeave(e: DragEvent): void {
    const zone = e.currentTarget as HTMLElement;
    const { left, top, right, bottom } = zone.getBoundingClientRect();
    if (e.clientX < left || e.clientX >= right || e.clientY < top || e.clientY >= bottom) {
      this._dragOver = false;
    }
  }

  private _onDrop(e: DragEvent): void {
    e.preventDefault();
    this._dragOver = false;
    if (this.disabled) return;
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length) this._addFiles(files);
  }

  // ── Public API ────────────────────────────────────────────────────────────────

  /** Current files in the queue. */
  get files(): File[] {
    return this._entries.map(e => e.file);
  }

  /**
   * Trigger upload of all queued files.
   * Fires `uwc-upload-handler` when `customUpload` is set, otherwise sends via XHR.
   */
  upload(): void {
    if (this._entries.length === 0) return;
    if (this.customUpload) {
      emit(this, 'uwc-upload-handler', { files: this._entries.map(e => e.file) } satisfies FileUploadHandlerEvent);
    } else {
      this._doUpload();
    }
  }

  /** Remove all queued files and reset the component state. */
  clear(): void {
    this._revokeAll();
    this._entries   = [];
    this._msgs      = [];
    this._progress  = 0;
    this._uploading = false;
    if (this._input) this._input.value = '';
    emit(this, 'uwc-clear', {} satisfies FileUploadClearEvent);
  }

  /**
   * Manually drive the progress bar (0–100).
   * Call from a `uwc-upload-handler` listener to show upload progress for custom uploads.
   * Setting to 0 hides the bar; setting to 100 completes and hides it after a brief delay.
   */
  setProgress(value: number): void {
    const v = Math.min(100, Math.max(0, Math.round(value)));
    this._progress  = v;
    this._uploading = v > 0 && v < 100;
    if (v >= 100) {
      setTimeout(() => { this._progress = 0; this._uploading = false; }, 600);
    }
  }

  /**
   * Animate the progress bar from 0 → 100 over `duration` ms.
   * Useful for demos or when the real upload duration is unknown.
   */
  simulateUpload(duration = 2000): void {
    if (this._entries.length === 0 || this._uploading) return;
    this._uploading = true;
    this._progress  = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const v = Math.min(100, Math.round(((now - start) / duration) * 100));
      this._progress = v;
      if (v < 100) {
        requestAnimationFrame(tick);
      } else {
        setTimeout(() => { this._uploading = false; this._progress = 0; }, 600);
      }
    };
    requestAnimationFrame(tick);
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────────

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._revokeAll();
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  override render() {
    return this.mode === 'basic' ? this._renderBasic() : this._renderAdvanced();
  }

  private _renderAdvanced() {
    const hasFiles     = this._entries.length > 0;
    const canUpload    = hasFiles && !this._uploading;
    const showUpload   = this.showUploadButton && !this.auto;
    const showCancel   = this.showCancelButton && !this.auto;

    return html`
      <div
        class=${classMap({
          'uwc-fileupload':           true,
          'uwc-fileupload--disabled': this.disabled,
          'uwc-fileupload--dragover': this._dragOver,
        })}
        part="base"
      >
        <!-- Hidden native file input -->
        <input
          class="uwc-fileupload__input"
          type="file"
          aria-hidden="true"
          tabindex="-1"
          ?multiple=${this.multiple}
          accept=${this.accept || nothing}
          @change=${this._onInputChange}
        >

        <!-- Toolbar -->
        <div class="uwc-fileupload__toolbar" part="toolbar">
          <slot name="header">
            <uwc-button
              part="choose-button"
              size="small"
              icon=${this.chooseIcon}
              ?disabled=${this.disabled}
              @click=${this._openChooser}
            >${this.chooseLabel}</uwc-button>

            ${showUpload || showCancel ? html`
              <div class="uwc-fileupload__toolbar-actions">
                ${showUpload ? html`
                  <uwc-button
                    part="upload-button"
                    size="small"
                    icon=${this.uploadIcon}
                    ?disabled=${this.disabled || !canUpload}
                    @click=${() => this.upload()}
                  >${this.uploadLabel}</uwc-button>
                ` : nothing}
                ${showCancel ? html`
                  <uwc-button
                    part="cancel-button"
                    size="small"
                    icon=${this.cancelIcon}
                    outline
                    ?disabled=${this.disabled || !canUpload}
                    @click=${() => this.clear()}
                  >${this.cancelLabel}</uwc-button>
                ` : nothing}
              </div>
            ` : nothing}
          </slot>
        </div>

        <!-- Content / drop zone -->
        <div
          class="uwc-fileupload__content"
          part="content"
          role="region"
          aria-label="File upload drop zone"
          @dragenter=${this._onDragEnter}
          @dragover=${this._onDragOver}
          @dragleave=${this._onDragLeave}
          @drop=${this._onDrop}
        >
          <!-- Validation messages -->
          ${this._msgs.length ? html`
            <div class="uwc-fileupload__messages" part="messages">
              ${repeat(this._msgs, m => m.id, m => html`
                <uwc-message
                  severity=${m.severity}
                  text=${m.text}
                  variant="outlined"
                  closable
                  @uwc-close=${() => { this._msgs = this._msgs.filter(msg => msg.id !== m.id); }}
                ></uwc-message>
              `)}
            </div>
          ` : nothing}

          <!-- Global progress bar — shown during XHR upload -->
          ${this._uploading ? html`
            <div class="uwc-fileupload__progress-wrap">
              <div class="uwc-fileupload__progress-header">
                <span>Uploading…</span>
                <span>${this._progress}%</span>
              </div>
              <div
                class="uwc-progress"
                part="progress"
                role="progressbar"
                aria-valuenow=${this._progress}
                aria-valuemin="0"
                aria-valuemax="100"
                aria-label="Upload progress"
                style="--uwc-progress-value: ${this._progress}%"
              ></div>
            </div>
          ` : nothing}

          <!-- File list -->
          ${hasFiles ? html`
            <div class="uwc-fileupload__files" part="files" role="list" aria-label="Selected files">
              ${repeat(this._entries, e => e.id, e => html`
                <div class="uwc-fileupload__file-row" part="file-row" role="listitem">
                  <!-- Thumbnail -->
                  <div
                    class="uwc-fileupload__thumbnail"
                    part="thumbnail"
                    style=${styleMap({ width: `${this.previewWidth}px`, height: `${this.previewWidth}px` })}
                    aria-hidden="true"
                  >
                    ${e.objectUrl ? html`
                      <img
                        class="uwc-fileupload__thumbnail-img"
                        src=${e.objectUrl}
                        alt=""
                        decoding="async"
                        width=${this.previewWidth}
                        height=${this.previewWidth}
                      >
                    ` : html`
                      <uwc-icon name="file-earmark" size="24px"></uwc-icon>
                    `}
                  </div>

                  <!-- File name + size -->
                  <div class="uwc-fileupload__file-info" part="file-info">
                    <span class="uwc-fileupload__filename" title=${e.file.name}>${e.file.name}</span>
                    <span class="uwc-fileupload__filesize">${this._formatSize(e.file.size)}</span>
                  </div>

                  <!-- Remove button -->
                  <uwc-button
                    part="remove-button"
                    size="small"
                    icon="x-lg"
                    icon-only
                    text
                    aria-label="Remove ${e.file.name}"
                    ?disabled=${this.disabled || this._uploading}
                    @click=${() => this._removeEntry(e)}
                  ></uwc-button>
                </div>
              `)}
            </div>
          ` : nothing}

          <!-- Empty state -->
          ${!hasFiles ? html`
            <div class="uwc-fileupload__empty" part="empty">
              <uwc-icon
                class="uwc-fileupload__empty-icon"
                name="cloud-upload"
                size="40px"
                aria-hidden="true"
              ></uwc-icon>
              <slot name="empty">
                <span>Drag and Drop files to here to upload.</span>
              </slot>
            </div>
          ` : nothing}
        </div>
      </div>
    `;
  }

  private _renderBasic() {
    const hasFile = this._entries.length > 0;

    return html`
      <div
        class=${classMap({
          'uwc-fileupload':           true,
          'uwc-fileupload--basic':    true,
          'uwc-fileupload--disabled': this.disabled,
        })}
        part="base"
      >
        <!-- Hidden file input -->
        <input
          class="uwc-fileupload__input"
          type="file"
          aria-hidden="true"
          tabindex="-1"
          ?multiple=${this.multiple}
          accept=${this.accept || nothing}
          @change=${this._onInputChange}
        >

        <uwc-button
          icon=${this.chooseIcon}
          ?disabled=${this.disabled}
          @click=${this._openChooser}
        >${this.chooseLabel}</uwc-button>

        <!-- Selected filename shown inline -->
        ${hasFile ? html`
          <span class="uwc-fileupload__basic-filename" title=${this._entries.map(e => e.file.name).join(', ')}>
            <uwc-icon name="file-earmark" size="0.9em" aria-hidden="true"></uwc-icon>
            ${this.multiple
              ? `${this._entries.length} file${this._entries.length > 1 ? 's' : ''} selected`
              : this._entries[0].file.name}
          </span>
        ` : nothing}

        <!-- Manual Upload button (non-auto, with URL or customUpload) -->
        ${hasFile && !this.auto && this.showUploadButton && (this.url || this.customUpload) ? html`
          <uwc-button
            size="small"
            icon=${this.uploadIcon}
            ?disabled=${this.disabled || this._uploading}
            @click=${() => this.upload()}
          >${this.uploadLabel}</uwc-button>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-fileupload': UwcFileupload; }
}
